import React from "react";
import { useNavigate } from "react-router-dom";
import Router from "./Router";
import { I18n } from "aws-amplify/utils";

import {
  withAuthenticator,
  Menu,
  MenuItem,
  Link,
  View,
  Heading,
  Flex,
} from "@aws-amplify/ui-react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Avatar } from "@mui/material";

import {
  signOut,
  getCurrentUser,
  AuthUser,
  fetchAuthSession,
} from "aws-amplify/auth";

import "@aws-amplify/ui-react/styles.css";

import LanguageMenu from "./components/LanguageMenu";

import "./App.css";
import { FaCopy } from "react-icons/fa6";
import { Helmet } from "react-helmet";

async function handleSignOut() {
  try {
    await signOut();
  } catch (error) {
    console.log("error signing out: ", error);
  }
}

async function sha256(data: string) {
  const encoder = new TextEncoder();
  const dataUint8 = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}

export function App() {
  const navigate = useNavigate();
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [email, setEmail] = React.useState<string>("nobody@virtualcoach.dance");
  const [groups, setGroups] = React.useState<string[]>([]);
  const [gravatar, setGravatar] = React.useState<string>(
    `https://gravatar.com/avatar/bba4291fb827b16a7b27926c5e9a917c43c367f06557306fcaa3ba24c7b3e0c5?s=40&d=robohash`
  );

  React.useEffect(() => {
    getCurrentUser().then((user) => {
      setUser(user);
    });
    fetchAuthSession().then((session) => {
      const groups =
        (session?.tokens?.idToken?.payload["cognito:groups"] as string[]) ??
        ([] as string[]);
      const email = session?.tokens?.idToken?.payload.email as string;
      setGroups(groups);
      setEmail(email);
    });
  }, []);

  React.useEffect(() => {
    sha256(email ?? "nobody@virtualcoach.dance").then((hash) => {
      setGravatar(`https://gravatar.com/avatar/${hash}?s=40&d=robohash`);
    });
  }, [email]);

  const year = new Date().getFullYear();
  const version = parseInt(import.meta.env.VITE_AWS_JOB_ID ?? 0);
  return (
    <>
      <Helmet>
        <title>{I18n.get("Virtual Dance Coach")}</title>
      </Helmet>
      <Flex direction="column" gap="1rem">
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" color={"transparent"}>
            <Toolbar>
              <Menu>
                <MenuItem onClick={() => navigate("/")}>
                  {I18n.get("Home")}
                </MenuItem>
                <MenuItem onClick={() => navigate("/dance-styles")}>
                  {I18n.get("Dance Styles")}
                </MenuItem>
                <MenuItem onClick={() => navigate("/patterns")}>
                  {I18n.get("Patterns")}
                </MenuItem>
                <MenuItem onClick={() => navigate("/routines")}>
                  {I18n.get("Routines")}
                </MenuItem>
              </Menu>
              <Typography
                variant="h6"
                component="div"
                sx={{ flexGrow: 1 }}
                textAlign="center"
              >
                VDC
              </Typography>

              <Menu
                trigger={
                  <Avatar
                    src={gravatar}
                    alt={`Profile Picture for ${user?.signInDetails?.loginId}`}
                  />
                }
                menuAlign="end"
              >
                <MenuItem
                  onClick={() =>
                    navigator.clipboard.writeText(user?.username ?? "")
                  }
                >
                  {user?.username}&nbsp;
                  <FaCopy aria-label={I18n.get("Copy ID")} />
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  {I18n.get("Sign Out")}
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
          <Router groups={groups} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Flex>
            <LanguageMenu />
          </Flex>
          <footer>
            &copy; {year} Osuuskunta Vinde &nbsp;|&nbsp;{I18n.get("Version")}{" "}
            {version}
          </footer>
        </Box>
      </Flex>
    </>
  );
}

const privacyTermsFooter = {
  Footer() {
    return (
      <View textAlign="center">
        <Link
          target="_blank"
          href={I18n.get("https://virtualcoach.dance/en-GB/privacy-policy/")}
        >
          {I18n.get("Privacy Policy")}
        </Link>
        &nbsp;|&nbsp;
        <Link
          target="_blank"
          href={I18n.get("https://virtualcoach.dance/en-GB/terms-condition/")}
        >
          {I18n.get("Terms of Service")}
        </Link>
      </View>
    );
  },
};

const components = {
  Header() {
    return (
      <View textAlign="center" marginBottom="1rem">
        <Heading level={1}>{I18n.get("Virtual Dance Coach")}</Heading>
      </View>
    );
  },
  Footer() {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Flex marginTop="1rem">
          <LanguageMenu />
        </Flex>
        <View textAlign="center" marginTop="1rem">
          &copy; {new Date().getFullYear()} Osuuskunta Vinde
        </View>
      </Box>
    );
  },
  SignUp: privacyTermsFooter,
  SignIn: privacyTermsFooter,
};

const AppWithAuth = withAuthenticator(App, {
  components,
  variation: "default",
});
export default AppWithAuth;
